import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SafeHtmlPipe } from '@core/pipes';
import { StorageService, StrapiService } from '@core/services';
import { PermissionsService } from '@customer/services';
import { HelpFeature, HelpTopic } from '@legacy-models/strapi/Help';
import {
  ContactsComponent as HelpContactsComponent,
  FaqsComponent as HelpFaqsComponent,
  RequestComponent as HelpRequestComponent
} from 'oneok/customer-portal/views/help/components';
import { BehaviorSubject } from 'rxjs';
import posthog from 'posthog-js';

interface SupportRequestItem {
  id: string;
  label: string;
  val?: string;
  subcategories?: SupportRequestItem[] | null;
  children?: SupportRequestItem[] | null;
}

interface SupportRequestContent {
  title: string;
  description: string;
  categories: SupportRequestItem[];
}

interface SupportRequest {
  content: SupportRequestContent;
  schema: Pick<HelpFeature, 'name' | 'type'>;
}

@Component({
  selector: 'oke-help',
  imports: [
    ReactiveFormsModule,
    AsyncPipe,
    SafeHtmlPipe,
    HelpContactsComponent,
    HelpFaqsComponent,
    HelpRequestComponent
  ],
  templateUrl: './help.html',
  styleUrl: './help.scss'
})
export class Help {
  contentSource = new BehaviorSubject<HelpTopic[]>([
    {
      topic: 'General',
      description: '<p>Find help unrelated to specific segment-driven business here!</p>',
      features: [
        {
          name: 'General FAQs',
          type: 'faq',
          faqs: {
            Other: {
              cat: 'Other',
              pairs: [
                {
                  q: 'How do I access a different module (feature) for my account?',
                  a: "<p>Reach out to your account administrator. You can see who your account (company) administrator(s) is on your <a href='/profile/acc_ID'>profile</a> page.</p>"
                }
              ]
            }
          }
        },
        {
          name: 'Submit a Request',
          type: 'request'
        }
      ]
    },
    {
      topic: 'Gathering & Processing',
      description: '<p>This is the <em>Gathering &amp; Processing</em> &#34;Help&#34; feature.</p>',
      features: [
        {
          name: 'G&amp;P FAQs',
          type: 'faq',
          faqs: {
            MeasuredVolumes: {
              cat: 'Measured Volumes',
              pairs: [
                {
                  q: "Why can't I find the meter I'm looking for within the list of meters for my account?",
                  a: "<p>ONEOK does not have your company set up as the Operator on the meter for the date range you have selected. If you believe this is incorrect, please contact <a href='mailto:divisionorder@oneok.com?subject=ONEOK%20Customer%20Portal'>divisionorder@oneok.com</a>.</p>"
                },
                {
                  q: 'Who can I contact with questions about measured volumes? (not allocated volume)',
                  a: '<p>Please refer to the G&amp;P contacts feature in Help Center to see whom you may reach out to for help.</p>'
                }
              ]
            },
            GasSettlement: {
              cat: 'Gas Settlement',
              pairs: [
                {
                  q: 'When will the gas statements be available?',
                  a: '<p>If all goes well, the statements are available by the last day of the month following the month of production.</p>'
                },
                {
                  q: 'Can I get excel statements?',
                  a: '<p>Yes, when you are in the Gas Settlement module please select Settlement Download where you will see an option to download in either CSV or PDF format.</p>'
                },
                {
                  q: 'Where can I get current month and prior month statements?',
                  a: '<p>We provide two years of prior month statements on the portal per the terms of your contract<br> If you do not have access to the Gas Settlement module you should contact your Account Administrator<br> If your Company did not own the assets in the prior period, you need to contact the previous owner for access to those statements.</p>'
                },
                {
                  q: 'Can I change the email distribution of the statements?',
                  a: "<p>Yes, please email <a href='mailto:OFSaccounting@oneok.com?subject=ONEOK%20Customer%20Portal'>OFSaccounting@oneok.com</a> and leave the producer&#8217;s name and contract number that needs the change of address. Give the email address of who should be taken off and who should be added.</p>"
                },
                {
                  q: 'What are the fees for that are showing on the statement?',
                  a: '<p>The fees are based on the producer&#8217;s contract, please revisit your contract.</p>'
                },
                {
                  q: 'Can I get the statements earlier in the month?',
                  a: '<p>ONEOK publishes the statements as soon as we finalize processing them.</p>'
                },
                {
                  q: 'Why does my Division Order % not equal the % on the statement?',
                  a: '<p>The statement shows the % of gas allocated for that meter&#8217;s contract, there can be multiple contracts per meter.</p>'
                },
                {
                  q: 'What is the price of the products or the residue based on?',
                  a: '<p>Please see the producer&#8217;s contract for the pricing.</p>'
                }
              ]
            },
            CheckDetails: {
              cat: 'Check Details',
              pairs: [
                {
                  q: 'What does the field on the check detail header labeled "off lease" indicate?',
                  a: '<p>It represents the fees charged for processing the gas according to your contract.</p>'
                },
                {
                  q: 'Who can I contact about these low volume metering fees?',
                  a: '<p>Please refer to the contacts card to view a list of representatives.</p>'
                },
                {
                  q: 'How do I contact the royalty relations group?',
                  a: "<p><strong>MAIL</strong><br>Oneok Field Services Company LLC<br>Attn: Royalty Relations<br>P.O. Box 871<br>Tulsa, OK 74102 </p><p><strong>CONTACT</strong><br> <a href='tel:+19185587463'>918.558.7463</a><br> <a href='mailto:divisionorder@oneok.com?subject=ONEOK%20Customer%20Portal'>divisionorder@oneok.com</a></p>"
                },
                {
                  q: 'What documents are needed to set up a Power of Attorney/Update Trustee/Individual or Corporate name change?',
                  a: "<p>Please send the appropriate name change documentation<br> Notarized Power of Attorne<br> Marriage Certificate or Court Order changing legal nam<br> Company or Corporation's name and changes due to merge<br>     - Certificate of Merger and verification of Tax I<br> Company or Coporation Change its name onl<br>     - Certificate of Name Change and verification of Tax ID</p>"
                },
                {
                  q: 'I have sold my interest, what now?',
                  a: '<p>Depending on what type of interest you have please provide the applicable document as well as W9 for any new owners. *<em>All documents must be recorded.</em><br> Royalty Interest - Mineral Dee<br> Working Interest - Assignment of Lease<br> Overriding Royalty Interest - Assignment of overriding royalty interest</p>'
                },
                {
                  q: 'What are the requirements for an Estate Proceeding? (_In all cases please include the heirs’ mailing addresses with completed W-9_)',
                  a: '<ul><li>Owner died with a Will (testate) and probate proceedings are to be conducted in the state where the property is locate<br>     - Death certificat<br>     - Order admitting will to Probat<br>     - Letters testamentar<br>     - Last will and testamen<br>     - Final decree of distribution or judgment of possessio<br> Owner died with a Will (testate) and probate proceedings are to be conducted in a state other than the state where the property is locate<br>     - Death certificat<br>     - Final decree of distribution or judgment of possessio<br>     - Documentation of ancillary probate proceedings opened in State where wells are located or an Affidavit of Heirshi<br> Owner died without a Will (intestate) Laws of descent and distribution will apply as relevan<br>     - Death certificat<br>     - Affidavit of heirship</li></ul>'
                },
                {
                  q: 'How do I change my address?',
                  a: "<p>Address changes must be received in writing. Please include your owner number, old address, new address, last 4 digits of your social security number or tax identification or you can complete the <a href='/nggp-documents/acc_ID'>Address Change form</a> and then mail or email the form.</p>"
                },
                {
                  q: 'How do I add or change direct deposit information?',
                  a: "<p>Please complete the <a href='/nggp-documents/acc_ID'>Direct Deposit form</a>.</p>"
                },
                {
                  q: 'Can I access my 1099 online?',
                  a: '<p>We do not provide online access for 1099s at his time. 1099s are sent by mail and should arrive no later than the first mailing day in February. To obtain a copy of your 1099 please contact the royalty relations group.</p>'
                },
                {
                  q: 'How do I claim my unclaimed property?',
                  a: "<ul><li>If you receive a letter from ONEOK, Inc. regarding unclaimed funds, please return the letter with the listed owner&#8217;s signature within 30 days to avoid the funds being escheated to the stat<br> You can visit the National Association of Unclaimed Property Administrators (NAUPA) website at <a href='https://www.unclaimed.org'>unclaimed.org</a>.</li></ul>"
                }
              ]
            }
          }
        },
        {
          name: 'G&amp;P Contacts',
          type: 'contacts'
        }
      ]
    }
  ]);
  content$ = this.contentSource.asObservable();
  supportRequest: SupportRequest = {
    content: {
      title: 'How can we help?',
      description:
        "<p>We're sorry you're experiencing an issue with the Customer Portal. Please select the appropriate options below to help us narrow down and better understand the problem. Use the description box and try to provide as much detail and context as possible. The more information you can provide, the more quickly we can resolve the issue. Expect to hear back from ONEOK within (2) business days.</p>",
      categories: [
        {
          label: 'Gathering & Processing',
          id: 'Gathering & Processing',
          val: 'NGGP',
          subcategories: [
            {
              label: 'Measured Volumes',
              id: 'type-measured-volumes',
              children: null
            },
            {
              label: 'Gas Settlement',
              id: 'type-gas-settlement',
              children: [
                {
                  label: 'Oklahoma',
                  id: 'Oklahoma',
                  val: '@team:100121[OFS Plant Accountants]'
                },
                {
                  label: 'Williston',
                  id: 'Williston',
                  val: '@team:100122[ORM Plant Accountants]'
                },
                {
                  label: 'Sage Creek',
                  id: 'Sage Creek',
                  val: '@team:100122[ORM Plant Accountants]'
                },
                {
                  label: 'Delaware',
                  id: 'Delaware',
                  val: '@team:100122[ORM Plant Accountants]'
                }
              ]
            },
            {
              label: 'Invoices',
              id: 'type-invoices',
              children: [
                {
                  label: 'Oklahoma',
                  id: 'Oklahoma',
                  val: '@team:100121[OFS Plant Accountants]'
                },
                {
                  label: 'Williston',
                  id: 'Williston',
                  val: '@team:100122[ORM Plant Accountants]'
                },
                {
                  label: 'Sage Creek',
                  id: 'Sage Creek',
                  val: '@team:100122[ORM Plant Accountants]'
                },
                {
                  label: 'Delaware',
                  id: 'Delaware',
                  val: '@team:100122[ORM Plant Accountants]'
                }
              ]
            },
            {
              label: 'Check Detail',
              id: 'type-check-detail',
              children: null
            },
            {
              label: 'Account/User Setup',
              id: 'type-account-user-setup',
              children: null
            },
            {
              label: 'Outage',
              id: 'type-outage',
              children: null
            }
          ]
        },
        {
          label: 'Pipelines',
          id: 'Pipelines',
          val: 'NGPIntrastate',
          subcategories: [
            {
              label: 'Access',
              id: 'type-user-access',
              children: null
            }
          ]
        },
        {
          label: "Other / I don't know",
          id: "Other / I don't know",
          subcategories: null
        }
      ]
    },
    schema: {
      name: 'Submit a Request',
      type: 'request'
    }
  };
  manager = new FormGroup({
    selectedTopic: new FormControl(''),
    selectedFeature: new FormControl<string | null>(null)
  });
  private readonly _storageSvc = inject(StorageService);
  private readonly _permissionsSvc = inject(PermissionsService);
  private readonly _strapiSvc = inject(StrapiService);

  constructor() {
    this._storageSvc.setCurrentPage('Help Center');
    this.content$.subscribe(this._onHelpContentDefined.bind(this));
    this.getHelpContent();
  }

  private _onHelpContentDefined(helpContent: HelpTopic[]): void {
    this.manager.get('selectedTopic')?.setValue(helpContent[0].topic);
    this.manager.get('selectedFeature')?.setValue(helpContent[0].features[0].type);
  }

  getHelpContent(): void {
    const portalHelpContent = JSON.parse(sessionStorage.getItem('helpContent') as string) as HelpTopic[] | null;
    if (!portalHelpContent) {
      this._strapiSvc.getHelpContent().subscribe((strapiHelpContent: HelpTopic[]) => {
        strapiHelpContent[0].features.push(this.supportRequest.schema);
        sessionStorage.setItem('helpContent', JSON.stringify(strapiHelpContent));
        this.contentSource.next(strapiHelpContent);
      });
    } else {
      this.contentSource.next(portalHelpContent);
    }
  }

  isTopicContextRequired(currentTopic: HelpTopic): boolean {
    return !!currentTopic.description || currentTopic.features.length > 1;
  }

  onTopicChanged(helpTopic: string): void {
    this.manager.get('selectedFeature')?.setValue(this.currentTopic.features[0].type);
    posthog.capture('help:topic_click', {
      topic_name: helpTopic
    });
  }

  get currentTopic(): HelpTopic {
    return this.contentSource.value.find(
      ({ topic }) => topic === this.manager.get('selectedTopic')!.value
    ) as HelpTopic;
  }

  get currentTopicFeature(): HelpFeature {
    let currentTopicFeature: HelpFeature = this.currentTopic.features[0];
    if (this.currentTopic.features.length > 1) {
      currentTopicFeature = this.currentTopic.features.find(
        (feature: HelpFeature) => feature.type === this.manager.get('selectedFeature')!.value
      ) as HelpFeature;
    }
    return currentTopicFeature;
  }

  protected trackFeatureChange(helpFeature: HelpFeature): void {
    posthog.capture('help:feature_click', {
      feature_name: helpFeature.name,
      parent_topic_name: this.currentTopic.topic
    });
  }

  hasPermission(topicLabel: string): boolean {
    let hasPermission = true;
    const nggpVals = ['Gathering & Processing', 'Gathering and Processing', 'NGGP', 'G&P'];
    const ngpVals = ['Pipelines', 'NGP'];
    const isNggp = nggpVals.map(val => val.toLowerCase()).includes(topicLabel.toLowerCase());
    const isNgp = ngpVals.map(val => val.toLowerCase()).includes(topicLabel.toLowerCase());
    if (isNggp && !this._permissionsSvc.hasNGGPBusinessSegment()) {
      hasPermission = false;
    } else if (isNgp && !this._permissionsSvc.hasNGPUserRole()) {
      hasPermission = false;
    }
    return hasPermission;
  }
}
